import { IpWebComponent } from '../../web-components/ip-web-component';
import { TIMELINE_EVENT_SLIDE_YEAR_CHANGE } from './timeline-constants';

const TimelineWrapperLargeYearTemplate = document.createElement('template');
TimelineWrapperLargeYearTemplate.innerHTML = `
<style>
    :host {
        --slide-year-min-height: 60vh;
        --year-font-size: 20rem;
        --font-color-title: rgba(0, 0, 0, 0.5);
        --slide-gap: 50px;
    }
    .timeline-wrapper {
        position: relative;
    }
    .slide-wrapper {
        z-index: 5;
        position: sticky;
        top: 0px;
        min-height: var(--slide-year-min-height);
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
    .year {
        text-align: center;
        width: 100%;
        height: var(--year-font-size);
        font-size: var(--year-font-size);
        display: flex;
        justify-content: center;
        color: var(--theme-primary-color);
        transform: scale(1.1);

        animation: init-animation-year 2s;
        animation-fill-mode: forwards;
        animation-delay: .5s;
        transition: color 0.2s;
    }
    .timeline-title {
        position: absolute;
        width: 100%;
        text-align: center;
        top: calc(var(--slide-year-min-height) / 2 + var(--year-font-size) / 2);
        font-size: calc(var(--year-font-size) / 5);
        margin: 1rem 0 0;
        opacity: 0;
        color: var(--font-color-title);

        animation: init-animation-title 2s;
        animation-fill-mode: forwards;
        animation-delay: .5s;
    }
    .slides-container {
        position: relative;
        z-index: 10;
    }

    slot {
        display: flex;
        flex-direction: column;
        gap: var(--slide-gap);
    }

    @media(max-width: 768px) {
        :host {
            --header-font-size: 1.8rem;
            --text-box-width: 50%;
            --slide-year-min-height: 500px;
            --year-font-size: 13rem;
        }
    }

    @media(max-width: 576px) {
        :host {
            --header-font-size: 1.6rem;
            --text-box-width: 100%;
            --slide-year-min-height: 400px;
            --year-font-size: 8rem;
        }
    }

    @keyframes init-animation-year {
        0% {
            transform: scale(1.1);
        }
        20% {
            transform: scale(1.2);
        }
        100% {
            transform: scale(1);
        }
    }

    @keyframes init-animation-title {
        0% {
            opacity: 0;
            transform: scale(0.9);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }
</style>
<div class="timeline-wrapper">
    <div class="slide-wrapper">
        <div class="year">
            <number-slider number=0></number-slider>
            <number-slider number=0></number-slider>
            <number-slider number=0></number-slider>
            <number-slider number=0></number-slider>
        </div>
    </div>
    <h1 class="timeline-title"></h1>
    <div class="slides-container">
        <slot></slot>
    </div>
</div>
`;

class TimelineWrapperLargeYear extends IpWebComponent {
    private unregisterAnimation: () => void = () => {};
    private numbers: HTMLElement[] = [];

    constructor() {
        super(TimelineWrapperLargeYearTemplate);
    }

    // Init component.
    connectedCallback() {
        // Find number elements.
        this.numbers = this.queryAll<HTMLElement>('number-slider');

        // Set init year content.
        const today = new Date(); // Start year out with "this year".
        this.setInitYear(today.getFullYear().toString());

        const animatedElmsInShadow = this.queryAll<HTMLElement>('.year');
        this.unregisterAnimation = this.startYearAnimOnEnterScreen(animatedElmsInShadow as HTMLElement[]);

        // Setup title content.
        const title = this.getAttribute('title');
        if (title) {
            const titleElm = this.queryUnsafe<HTMLElement>('.timeline-title');
            titleElm.textContent = title;
        }

        // Setup event listener for year change.
        const timelineWrapperElm = this.queryUnsafe<HTMLElement>('.timeline-wrapper');
        timelineWrapperElm.addEventListener(TIMELINE_EVENT_SLIDE_YEAR_CHANGE, (e) => {
            const slideYearEvent = e as CustomEvent<string>;
            this.setYear(slideYearEvent.detail);
        });

        // Setup timeline wrapper.
        this.setupTimelineWrapper(timelineWrapperElm);

        // Setup colour change of year on scroll.
        this.setupYearColorInterpolation(this.queryUnsafe<HTMLElement>('.slide-wrapper'));
    }

    disconnectedCallback() {
        this.unregisterAnimation();
    }

    /** Sets up the wrapper for all slides, for instance the gap between slides. */
    setupTimelineWrapper(timelineWrapperElm: HTMLElement) {
        const slideGap = (this.getAttribute('slide-gap') || '50') + 'px';
        timelineWrapperElm.style.setProperty('--slide-gap', slideGap);
    }

    setInitYear(year: string) {
        if (!year || year.length !== 4) return;
        for (let i = 0; i < this.numbers.length; i++) {
            this.numbers[i]?.setAttribute('init-number', year.substring(i, i + 1));
        }
    }
    setYear(year: string) {
        if (!year || year.length !== 4) return;
        for (let i = 0; i < this.numbers.length; i++) {
            this.numbers[i]?.setAttribute('number', year.substring(i, i + 1));
        }
        this.shadowRoot?.querySelector('.year')?.classList.add('animate-color');
    }

    startYearAnimOnEnterScreen(elms: HTMLElement[] | undefined) {
        if (!elms || elms.length === 0) return () => {};

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            this.setYear(this.getAttribute('year') || '');
                        }, 500); // Delay before changing year.
                    }
                });
            },
            { rootMargin: '-40% 0px' }
        );
        elms.forEach((elm) => {
            observer.observe(elm);
        });

        // Return function that unobserves the element.
        return () => {
            observer.disconnect();
        };
    }

    setupYearColorInterpolation(elm: HTMLElement) {
        const getCssVariableValue = (variableName: string): string | null => {
            const styles = getComputedStyle(document.documentElement);
            return styles.getPropertyValue(variableName).trim() || null;
        };

        const hexToRgba = (hex: string, opacity: number): number[] => {
            const hexValue = hex.replace('#', '');
            const r = parseInt(hexValue.substring(0, 2), 16);
            const g = parseInt(hexValue.substring(2, 4), 16);
            const b = parseInt(hexValue.substring(4, 6), 16);
            return [r, g, b, opacity];
        };

        const calculateScrollPercentage = (elm: HTMLElement, maxScroll: number): number => {
            const elmY = elm.getBoundingClientRect().top;
            const yPos = window.scrollY - elmY;

            const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);
            const clampedYPos = clamp(yPos, 0, maxScroll);
            const percentage = clampedYPos / maxScroll;
            return percentage;
        };

        const initColour = hexToRgba(getCssVariableValue('--theme-primary-color') || '#de3000', 1);
        const endColour = [0, 0, 0, 0.05];
        const yearElm = this.queryUnsafe<HTMLElement>('.year');
        document.addEventListener('scroll', () => {
            // Calculate the scroll percentage of the element out of the max scroll amount.
            const percentage = calculateScrollPercentage(elm, 500);
            // Get an interpolated colour based on the percentage.
            const colour = this.interpolateColor(initColour, endColour, percentage);
            // Set the colour of the year element.
            yearElm.style.color = `rgba(${colour[0]}, ${colour[1]}, ${colour[2]}, ${colour[3]})`;
        });
    }

    /**
     *
     * @param color1 Start RGB (or RGBA).
     * @param color2 End RGB (or RGBA).
     * @param percentage Percentage of interpolation (0-1).
     * @returns An array of the interpolated color.
     */
    interpolateColor(color1: number[], color2: number[], percentage: number) {
        if (color1.length != color2.length) {
            throw new Error('Start and end colors must have the same number of components');
        }

        const resultColour: number[] = [];
        for (let i = 0; i < color1.length; i++) {
            const color1Entry = color1[i] as number;
            const color2Entry = color2[i] as number;
            resultColour.push(color1Entry + (color2Entry - color1Entry) * percentage);
        }
        return resultColour;
    }
}

// Define element, if not already defined.
if (customElements.get('timeline-wrapper-large-year') === undefined) {
    customElements.define('timeline-wrapper-large-year', TimelineWrapperLargeYear);
}
